import { PropsWithChildren, useEffect, useState } from 'react';
import { AccountInfo, AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adApiTokenConfig } from './msalInstance';
import { Box } from '@mui/material';

import { CircleWavyWarning } from '@phosphor-icons/react';
import { ErrorMessage } from '../../components/ErrorMessage';
import { FullscreenCircularProgress } from '../../components/FullscreenCircularProgress';
import { handleLogout } from '../../utils/signOut';
import { getOrganizationId, getTokenSilent } from './helpers';
import { Role } from '../../services/types/organization';
import { AuthContext } from './authContextInit';

// https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/2084#issuecomment-675013771
type IdTokenClaims = {
  name: string;
  roles: string[];
  groups: string[];
};

export interface UsersAuthData {
  org_id: string;
  roles: Role[];
  id: string;
}

export interface IAuthContext {
  usersAuthData: UsersAuthData;
}

const PATHNAME_BEFORE_REDIRECT = 'PATHNAME_BEFORE_REDIRECT';

const popLocationBeforeRedirect = () => {
  const locationBeforeRedirect = window.localStorage.getItem(PATHNAME_BEFORE_REDIRECT);
  window.localStorage.removeItem(PATHNAME_BEFORE_REDIRECT);
  return locationBeforeRedirect;
};

const setLocationBeforeRedirect = (locationPath: string) =>
  window.localStorage.setItem(PATHNAME_BEFORE_REDIRECT, locationPath);

export const AuthProvider = (props: PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [authValues, setAuthValues] = useState<IAuthContext | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const setAuthValuesToState = async (
    adApiTokenResponse: AuthenticationResult,
    currentUserId: string
  ) => {
    const { roles } = adApiTokenResponse.idTokenClaims as IdTokenClaims;
    const orgId = await getOrganizationId(setError);

    setAuthValues({
      usersAuthData: {
        roles: roles as Role[],
        org_id: orgId,
        id: currentUserId
      }
    });
  };

  useEffect(() => {
    const getTokenAndSetToState = async (account: AccountInfo) => {
      const tokenResponse = await getTokenSilent();
      if (tokenResponse) {
        setAuthValuesToState(tokenResponse, account.localAccountId);
      }
    };
    const setTokenToStateIfActiveAccountExists = () => {
      const activeAccount = instance.getActiveAccount();
      if (activeAccount) {
        getTokenAndSetToState(activeAccount);
      }
    };
    setTokenToStateIfActiveAccountExists();
    const callbackId = instance.addEventCallback(async (message: EventMessage) => {
      const setActiveAccountAndGetToken = async () => {
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          const currentUserAccount = accounts[0];
          (window as any).heap?.addUserProperties({ email: currentUserAccount.username }); //add heap username
          instance.setActiveAccount(currentUserAccount);
          getTokenAndSetToState(currentUserAccount);
        }
      };
      switch (message.eventType) {
        case EventType.LOGIN_SUCCESS:
          await setActiveAccountAndGetToken();
          const locationBeforeRedirect = popLocationBeforeRedirect();
          if (locationBeforeRedirect) navigate(locationBeforeRedirect);
          break;
        case EventType.LOGIN_FAILURE:
          console.error('Login failed:', message.error);
          break;
        case EventType.SSO_SILENT_SUCCESS: {
          setActiveAccountAndGetToken();
          break;
        }
        case EventType.SSO_SILENT_FAILURE: {
          setIsRedirecting(true);
          setLocationBeforeRedirect(location.pathname);
          instance.acquireTokenRedirect(adApiTokenConfig).catch(console.error);
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isRedirecting) return <FullscreenCircularProgress />;

  return (
    <>
      {!error ? (
        <AuthContext.Provider value={authValues} {...props} />
      ) : (
        <Box height="90vh" display="flex" alignItems="center" justifyContent="center">
          <ErrorMessage
            error={'User Organization Error'}
            Icon={CircleWavyWarning}
            message={error}
            action={{
              text: 'Log out',
              onClick: () => handleLogout(instance, isAuthenticated, inProgress)
            }}
          />
        </Box>
      )}
    </>
  );
};
